/**
 * EntityTile - Professional tile component for displaying entities
 *
 * A visually polished, reusable tile component with:
 * - Elevated card design with shadows
 * - Smooth hover animations
 * - Color-coded indicators
 * - Professional badge system
 * - Responsive grid-friendly layout
 */

import { ReactNode, useState, CSSProperties } from 'react';
import { MoreVertical, ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';

// Badge variants
export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

export interface TileBadge {
  label: string;
  variant?: BadgeVariant;
  icon?: LucideIcon;
}

// Stat display config
export interface TileStat {
  label: string;
  value: string | number;
  icon?: LucideIcon;
}

// Action button config
export interface TileAction {
  icon?: LucideIcon;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

// Main tile props
export interface EntityTileProps {
  // Content
  title: string;
  subtitle?: ReactNode;
  description?: string;
  icon?: LucideIcon;
  avatar?: ReactNode;
  color?: string;
  colorBar?: string;
  thumbnail?: ReactNode;

  // Metadata
  badges?: TileBadge[];
  stats?: TileStat[];
  metadata?: ReactNode;
  footer?: ReactNode;

  // Actions
  actions?: TileAction[];
  onClick?: () => void;
  onDoubleClick?: () => void;

  // Expansion
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  expandedContent?: ReactNode;

  // Styling
  variant?: 'compact' | 'standard' | 'large' | 'card';
  selected?: boolean;
  disabled?: boolean;
  loading?: boolean;
  elevated?: boolean;
  style?: CSSProperties;
  className?: string;

  // Accessibility
  ariaLabel?: string;
}

// Enhanced badge colors with better contrast
const BADGE_COLORS: Record<BadgeVariant, { bg: string; text: string; border: string }> = {
  default: { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' },
  primary: { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
  success: { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
  warning: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  danger: { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
  info: { bg: '#e0f2fe', text: '#0369a1', border: '#bae6fd' },
};

// Variant configurations
const VARIANT_STYLES = {
  compact: {
    padding: '12px 14px',
    gap: '12px',
    titleSize: '14px',
    subtitleSize: '12px',
    avatarSize: 36,
    colorBarWidth: 4,
    shadow: '0 1px 2px rgba(0,0,0,0.05)',
    hoverShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  standard: {
    padding: '16px 18px',
    gap: '14px',
    titleSize: '15px',
    subtitleSize: '13px',
    avatarSize: 44,
    colorBarWidth: 5,
    shadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    hoverShadow: '0 8px 25px rgba(0,0,0,0.12)',
  },
  large: {
    padding: '20px 22px',
    gap: '16px',
    titleSize: '17px',
    subtitleSize: '14px',
    avatarSize: 52,
    colorBarWidth: 6,
    shadow: '0 2px 4px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    hoverShadow: '0 12px 30px rgba(0,0,0,0.15)',
  },
  card: {
    padding: '0',
    gap: '0',
    titleSize: '16px',
    subtitleSize: '13px',
    avatarSize: 48,
    colorBarWidth: 0,
    shadow: '0 2px 8px rgba(0,0,0,0.08)',
    hoverShadow: '0 12px 30px rgba(0,0,0,0.15)',
  },
};

function Badge({ badge }: { badge: TileBadge }) {
  const colors = BADGE_COLORS[badge.variant || 'default'];
  const Icon = badge.icon;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 10px',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.02em',
        borderRadius: '6px',
        background: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        whiteSpace: 'nowrap',
        textTransform: 'uppercase',
      }}
    >
      {Icon && <Icon size={12} />}
      {badge.label}
    </span>
  );
}

export function EntityTile({
  title,
  subtitle,
  description,
  icon: Icon,
  avatar,
  color,
  colorBar,
  thumbnail,
  badges = [],
  stats = [],
  metadata,
  footer,
  actions = [],
  onClick,
  onDoubleClick,
  expandable = false,
  expanded = false,
  onToggleExpand,
  expandedContent,
  variant = 'standard',
  selected = false,
  disabled = false,
  loading = false,
  elevated = true,
  style,
  className,
  ariaLabel,
}: EntityTileProps) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const sizing = VARIANT_STYLES[variant];
  const isClickable = !!onClick || expandable;
  const showExpander = expandable && expandedContent;
  const isCardVariant = variant === 'card';

  const handleClick = () => {
    if (disabled || loading) return;
    if (expandable && onToggleExpand) {
      onToggleExpand();
    } else if (onClick) {
      onClick();
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: TileAction) => {
    e.stopPropagation();
    if (!action.disabled) {
      action.onClick(e);
    }
    setActionsOpen(false);
  };

  // Helper to render avatar (can be string URL or ReactNode)
  const renderAvatar = (size: number, forCard = false) => {
    if (!avatar && !Icon) return null;

    // If avatar is a ReactNode (not a string), render it directly
    if (avatar && typeof avatar !== 'string') {
      return avatar;
    }

    // Avatar is a URL string
    const avatarUrl = avatar as string;
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: avatarUrl ? '50%' : forCard ? '10px' : '12px',
          background: avatarUrl
            ? `url(${avatarUrl}) center/cover`
            : color
              ? `${color}18`
              : '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: color || '#64748b',
          border: avatarUrl ? '2px solid #f1f5f9' : 'none',
        }}
      >
        {!avatarUrl && Icon && <Icon size={size * 0.45} />}
      </div>
    );
  };

  // Card variant with thumbnail
  if (isCardVariant && thumbnail) {
    const isThumbnailString = typeof thumbnail === 'string';
    return (
      <div
        className={`entity-tile entity-tile-card ${className || ''}`}
        style={{
          background: 'white',
          borderRadius: '16px',
          border: selected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
          boxShadow: selected
            ? '0 0 0 3px rgba(59, 130, 246, 0.2)'
            : hovered && elevated
              ? sizing.hoverShadow
              : elevated
                ? sizing.shadow
                : 'none',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease',
          transform: hovered && !disabled ? 'translateY(-2px)' : 'none',
          overflow: 'hidden',
          ...style,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setActionsOpen(false);
        }}
        aria-label={ariaLabel || title}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={(e) => {
          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {/* Thumbnail */}
        <div
          onClick={handleClick}
          style={{
            width: '100%',
            cursor: isClickable ? 'pointer' : 'default',
            position: 'relative',
          }}
        >
          {isThumbnailString ? (
            <div
              style={{
                width: '100%',
                aspectRatio: '16/9',
                background: `url(${thumbnail}) center/cover`,
                backgroundColor: '#f1f5f9',
              }}
            />
          ) : (
            thumbnail
          )}
          {/* Color accent bar */}
          {(color || colorBar) && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 4,
                background: colorBar || color,
              }}
            />
          )}
          {/* Status badges overlay */}
          {badges.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              {badges.slice(0, 2).map((badge, i) => (
                <Badge key={i} badge={badge} />
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            {/* Avatar/Icon for card */}
            {(avatar || Icon) && renderAvatar(40, true)}

            <div style={{ flex: 1, minWidth: 0 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: sizing.titleSize,
                  fontWeight: 600,
                  color: '#1e293b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </h3>
              {subtitle && (
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: sizing.subtitleSize,
                    color: '#64748b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>

            {/* Actions */}
            {actions.length > 0 && (
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionsOpen(!actionsOpen);
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '8px',
                    background: actionsOpen ? '#f1f5f9' : 'transparent',
                    color: '#64748b',
                    cursor: 'pointer',
                  }}
                >
                  <MoreVertical size={18} />
                </button>
                {actionsOpen && <ActionMenu actions={actions} onAction={handleActionClick} />}
              </div>
            )}
          </div>

          {/* Stats row */}
          {stats.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 20,
                marginTop: 14,
                paddingTop: 14,
                borderTop: '1px solid #f1f5f9',
              }}
            >
              {stats.map((stat, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {stat.icon && <stat.icon size={14} style={{ color: '#94a3b8' }} />}
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                    {stat.value}
                  </span>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>{stat.label}</span>
                </div>
              ))}
            </div>
          )}

          {footer}
        </div>
      </div>
    );
  }

  // Standard tile layout
  return (
    <div
      className={`entity-tile ${className || ''}`}
      style={{
        background: 'white',
        borderRadius: '14px',
        border: selected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
        boxShadow: selected
          ? '0 0 0 3px rgba(59, 130, 246, 0.15)'
          : hovered && elevated
            ? sizing.hoverShadow
            : elevated
              ? sizing.shadow
              : 'none',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease',
        transform: hovered && !disabled && elevated ? 'translateY(-1px)' : 'none',
        overflow: 'hidden',
        ...style,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setActionsOpen(false);
      }}
      aria-label={ariaLabel || title}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Main tile content */}
      <div
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: sizing.gap,
          padding: sizing.padding,
          cursor: isClickable && !disabled ? 'pointer' : 'default',
          background: expanded ? '#f8fafc' : 'transparent',
        }}
      >
        {/* Color bar indicator */}
        {(colorBar || color) && sizing.colorBarWidth > 0 && (
          <div
            style={{
              width: sizing.colorBarWidth,
              alignSelf: 'stretch',
              borderRadius: sizing.colorBarWidth,
              background: colorBar || color,
              flexShrink: 0,
              marginLeft: -Number(sizing.padding.split(' ')[1]?.replace('px', '') || 16) + 8,
            }}
          />
        )}

        {/* Avatar or Icon */}
        {(avatar || Icon) && renderAvatar(sizing.avatarSize)}

        {/* Content area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span
              style={{
                fontWeight: 600,
                fontSize: sizing.titleSize,
                color: '#1e293b',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </span>
            {badges.length > 0 && variant !== 'compact' && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {badges.slice(0, 3).map((badge, i) => (
                  <Badge key={i} badge={badge} />
                ))}
                {badges.length > 3 && (
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#94a3b8',
                      fontWeight: 500,
                    }}
                  >
                    +{badges.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Subtitle */}
          {subtitle && (
            <div
              style={{
                fontSize: sizing.subtitleSize,
                color: '#64748b',
                marginTop: '3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </div>
          )}

          {/* Description */}
          {description && variant === 'large' && (
            <div
              style={{
                fontSize: '13px',
                color: '#64748b',
                marginTop: '8px',
                lineHeight: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {description}
            </div>
          )}

          {/* Badges for compact */}
          {badges.length > 0 && variant === 'compact' && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
              {badges.slice(0, 3).map((badge, i) => (
                <Badge key={i} badge={badge} />
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        {stats.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: '20px',
              flexShrink: 0,
              padding: '8px 16px',
              background: '#f8fafc',
              borderRadius: '10px',
            }}
          >
            {stats.map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#1e293b',
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginTop: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    fontWeight: 500,
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {metadata}

        {/* Actions */}
        {actions.length > 0 && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {actions.length <= 2 ? (
              <div style={{ display: 'flex', gap: '6px' }}>
                {actions.map((action, i) => {
                  const ActionIcon = action.icon;
                  return (
                    <button
                      key={i}
                      onClick={(e) => handleActionClick(e, action)}
                      disabled={action.disabled}
                      title={action.label}
                      aria-label={action.label}
                      style={{
                        minWidth: 36,
                        height: 36,
                        padding: ActionIcon ? 0 : '0 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        background: hovered ? '#f8fafc' : 'white',
                        color: action.variant === 'danger' ? '#dc2626' : '#64748b',
                        cursor: action.disabled ? 'not-allowed' : 'pointer',
                        opacity: action.disabled ? 0.5 : 1,
                        transition: 'all 0.15s',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      {ActionIcon && <ActionIcon size={16} />}
                      {!ActionIcon && action.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionsOpen(!actionsOpen);
                  }}
                  aria-label="More actions"
                  aria-expanded={actionsOpen}
                  style={{
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    background: actionsOpen ? '#f1f5f9' : hovered ? '#f8fafc' : 'white',
                    color: '#64748b',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <MoreVertical size={16} />
                </button>
                {actionsOpen && <ActionMenu actions={actions} onAction={handleActionClick} />}
              </>
            )}
          </div>
        )}

        {/* Expand indicator */}
        {showExpander && (
          <div
            style={{
              flexShrink: 0,
              color: '#94a3b8',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              background: expanded ? '#f1f5f9' : 'transparent',
              transition: 'all 0.15s',
            }}
          >
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '2px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              animation: 'spin 0.8s linear infinite',
              flexShrink: 0,
            }}
          />
        )}
      </div>

      {/* Footer */}
      {footer && !expanded && (
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid #f1f5f9',
            background: '#fafafa',
          }}
        >
          {footer}
        </div>
      )}

      {/* Expanded content */}
      {showExpander && expanded && (
        <div
          style={{
            borderTop: '1px solid #e5e7eb',
            background: '#fafafa',
          }}
        >
          {expandedContent}
        </div>
      )}
    </div>
  );
}

// Action menu component
function ActionMenu({
  actions,
  onAction,
}: {
  actions: TileAction[];
  onAction: (e: React.MouseEvent, action: TileAction) => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 6,
        minWidth: 160,
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
        zIndex: 50,
        overflow: 'hidden',
        padding: '6px',
      }}
    >
      {actions.map((action, i) => {
        const ActionIcon = action.icon;
        return (
          <button
            key={i}
            onClick={(e) => onAction(e, action)}
            disabled={action.disabled}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 12px',
              border: 'none',
              borderRadius: '8px',
              background: 'transparent',
              color: action.variant === 'danger' ? '#dc2626' : '#374151',
              fontSize: '14px',
              fontWeight: 500,
              cursor: action.disabled ? 'not-allowed' : 'pointer',
              opacity: action.disabled ? 0.5 : 1,
              textAlign: 'left',
              transition: 'background 0.1s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            {ActionIcon && <ActionIcon size={16} />}
            {action.label}
          </button>
        );
      })}
    </div>
  );
}

export default EntityTile;
